import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { LIST_PAGES, LIST_PANE_DEFAULT_SHARE, LIST_PANE_KEY_STEP_PX, LIST_PANE_WIDTH_SCOPE_KEY, RECORD_ICONS, clampListPaneShare, listPaneRendering, listPaneScopeKey, readListPanePreference, readListPaneShare, t, type ListPageSpec, type PagedRecord } from '@bms2/shared';
import { Icon } from '../../components/Icon';
import { ListPage, listInputsQuery } from './ListPage';
import { SAVED_VIEWS_PATH, put, useApi } from './api';
import { ListPaneOpenContext } from './listPaneOpen';
import styles from './ListDetailPage.module.css';

/**
 * 🔒 The list-and-detail frame every list-backed record renders in (decision
 * 0408, generalising decisions 0318 and 0326 off the catalog). At the list
 * route the list stands alone at full width. Picking a row narrows it to a
 * third on the left and opens that record's own page at two thirds on the
 * right, the URL carrying the pick, so a deep link opens the record and the
 * list stays mounted with its search and its scroll across the pick.
 *
 * 🔨 The pane collapses to a bar, and collapsing is a distinct rendering mode
 * rather than a width transition on the same markup — the rule the navigation
 * rail already keeps (piece 12). The bar carries the list's own glyph and the
 * expand control and nothing else. 🔒 The collapsed state is a `SavedView` row
 * on the pane's own scope, one per person and per list.
 *
 * 🔒 The pane's width is the person's to drag by the handle on the seam (decision
 * 7781; T3163), between a floor that holds its primary column and a ceiling that
 * leaves the record its narrowest readable width. A double press or Enter returns
 * the third, and the width is one row per person for every list page.
 */
export interface ListDetailPageProps {
  spec: ListPageSpec;
  /** What the right-hand pane draws for the picked record; the frame never knows which page that is. */
  detail: (recordId: string) => ReactNode;
  apiBaseUrl?: string;
  /**
   * The picked record where the route names it under another parameter: the bill
   * of materials' address under its Deal draws this frame too (decision 1052).
   * Absent, the route's own `:id` is the pick.
   */
  recordId?: string;
}

export function ListDetailPage({ spec, detail, apiBaseUrl = '', recordId }: ListDetailPageProps) {
  const params = useParams();
  const id = recordId ?? params.id ?? '';
  const [search] = useSearchParams();
  // 🔒 Back returns to the list the record was picked from, search, filters and saved view all (decisions 0762, 7750).
  const backTo = `${spec.route}${listInputsQuery(spec, search, 'page')}`;
  const scopeKey = listPaneScopeKey(spec.route);
  const preference = useApi<{ layout: unknown }>(`${SAVED_VIEWS_PATH}/layout?scopeKey=${encodeURIComponent(scopeKey)}`, apiBaseUrl);
  const remembered = preference.state.kind === 'ready' ? readListPanePreference({ layout: preference.state.data.layout }).collapsed : null;
  // The person's own click wins over the stored row from the moment it happens, so the bar answers at once.
  const [chosen, setChosen] = useState<boolean | null>(null);
  const collapsed = chosen ?? remembered ?? false;
  /**
   * 🔒 A list the server refused, beside a record the viewer opened, is withheld:
   * no pane, no bar and no Back link to it, and the record takes the whole width
   * (T218; decision 0693). An outside login lands on its own company this way.
   */
  // Keyed on the record type, so a refusal on one list is never carried to the next list this frame draws.
  const [refusedFor, setRefusedFor] = useState<string | null>(null);
  const onRefused = useCallback(() => setRefusedFor(spec.recordType), [spec.recordType]);
  const rendering = listPaneRendering(collapsed, refusedFor === spec.recordType && id !== '');
  const withheld = rendering === 'withheld';
  const choose = (next: boolean) => {
    setChosen(next);
    void put(SAVED_VIEWS_PATH, { scopeKey, layout: { collapsed: next } }, apiBaseUrl);
  };
  const icon = RECORD_ICONS[spec.recordType] ?? null;
  const { attachFrame, ...width } = usePaneWidth(apiBaseUrl);
  const splitStyle = width.share === null ? undefined : ({ '--list-pane-share': String(width.share) } as CSSProperties);

  return (
    <div ref={attachFrame} className={styles.split} style={splitStyle} data-list-detail={id ? 'detail' : 'list'} data-list-pane-mode={id ? rendering : 'pane'} data-list-detail-record={spec.recordType}>
      <div className={styles.list} data-list-detail-pane="list">
        <ListPage
          spec={spec}
          apiBaseUrl={apiBaseUrl}
          frame={id ? 'pane' : 'page'}
          activeId={id || null}
          {...(id ? { onCollapsePane: () => choose(true), onRefused } : {})}
        />
        {/* 🔒 The handle on the seam stands while the pane is drawn beside a record, from the desktop breakpoint (decision 7781). */}
        {id && rendering === 'pane' ? <PaneHandle width={width} /> : null}
      </div>
      {id && !withheld ? (
        <div className={styles.bar} data-list-detail-pane="bar">
          <button type="button" className={styles.barButton} onClick={() => choose(false)} data-list-detail-expand="true" aria-label={t('listDetail.expand')} title={t('listDetail.expand')}>
            {icon ? <Icon meaning={icon} size="nav" /> : null}
            <Icon meaning="expandPanel" size="nav" />
          </button>
        </div>
      ) : null}
      {id ? (
        <div className={styles.detail} data-list-detail-pane="detail">
          {withheld ? null : <Link to={backTo} className={styles.back} data-list-detail-back="true">{t('listDetail.back')}</Link>}
          {/* 🔒 The record reads whether the list stands open beside it, so a document folds its notes rail under its sheet below 1280 (T3685; decision 11540). */}
          <ListPaneOpenContext.Provider value={rendering === 'pane'}>{detail(id)}</ListPaneOpenContext.Provider>
        </div>
      ) : null}
    </div>
  );
}

/** The pane's width as the frame draws it and the handle moves it (decision 7781). */
interface PaneWidth {
  attachFrame: (element: HTMLDivElement | null) => void;
  /** The share drawn now, held between the floor and the ceiling; null draws the third the stylesheet falls back to. */
  share: number | null;
  /** The share the handle reads out, the third where nothing is stored. */
  shown: number;
  frameWidth: number;
  measure: () => { left: number; width: number };
  /** Draws a share held between the floor and the ceiling, and answers it. */
  draw: (share: number) => number;
  /** Writes a share as the person's own, or the third where it is null. */
  save: (share: number | null) => void;
  /** Draws the third again and writes it. */
  reset: () => void;
}

/** 🔨 A keyboard change is saved once the keys rest, so a held arrow writes one row and not twenty. */
const KEY_SAVE_DELAY_MS = 400;

/**
 * 🔒 The person's one pane width (decision 7781): read from the `listPaneWidth` row, drawn as a share
 * of the frame, and written when a drag ends or the keys rest. The person's own move wins over the
 * stored row from the moment it happens, as the collapse control's does.
 */
function usePaneWidth(apiBaseUrl: string): PaneWidth {
  const stored = useApi<{ layout: unknown }>(`${SAVED_VIEWS_PATH}/layout?scopeKey=${encodeURIComponent(LIST_PANE_WIDTH_SCOPE_KEY)}`, apiBaseUrl);
  const remembered = stored.state.kind === 'ready' ? readListPaneShare({ layout: stored.state.data.layout }) : null;
  // Undefined until the person moves the handle; null once they press it back to the third.
  const [chosen, setChosen] = useState<number | null | undefined>(undefined);
  const [frame, setFrame] = useState<HTMLDivElement | null>(null);
  const [frameWidth, setFrameWidth] = useState(0);
  const measure = useCallback(() => {
    const rect = frame?.getBoundingClientRect();
    return { left: rect?.left ?? 0, width: rect?.width ?? 0 };
  }, [frame]);
  useLayoutEffect(() => {
    if (!frame) return;
    const read = () => setFrameWidth(frame.getBoundingClientRect().width);
    read();
    // 🔒 The frame itself is watched, so a collapsed navigation rail moves the handle's limits too, not only a window resize (T3168).
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(read);
    observer?.observe(frame);
    window.addEventListener('resize', read);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', read);
    };
  }, [frame]);
  const save = useCallback((share: number | null) => void put(SAVED_VIEWS_PATH, { scopeKey: LIST_PANE_WIDTH_SCOPE_KEY, layout: share === null ? {} : { paneShare: share } }, apiBaseUrl), [apiBaseUrl]);
  const raw = chosen === undefined ? remembered : chosen;
  const share = raw === null ? null : clampListPaneShare(raw, frameWidth);
  const draw = (next: number) => {
    const held = clampListPaneShare(next, measure().width);
    setChosen(held);
    return held;
  };
  const reset = () => {
    setChosen(null);
    save(null);
  };
  return { attachFrame: setFrame, share, shown: share ?? clampListPaneShare(LIST_PANE_DEFAULT_SHARE, frameWidth), frameWidth, measure, draw, save, reset };
}

/**
 * 🔒 The handle on the seam between the pane and the record (decision 7781): a vertical separator in
 * the tab order. A drag moves the pane and saves as it ends; the arrows move it a step, Home and End
 * take it to the floor and the ceiling, and Enter or a double press returns the third.
 */
function PaneHandle({ width }: { width: Omit<PaneWidth, 'attachFrame'> }) {
  const drag = useRef<{ left: number; width: number; moved: boolean } | null>(null);
  /** A keyboard change waiting for the keys to rest, written at once if the handle goes first (the pane collapsing). */
  const pending = useRef<{ timer: ReturnType<typeof setTimeout>; share: number } | null>(null);
  const { save } = width;
  useEffect(() => () => {
    const waiting = pending.current;
    if (waiting === null) return;
    clearTimeout(waiting.timer);
    save(waiting.share);
  }, [save]);
  const cancelPending = () => {
    if (pending.current !== null) clearTimeout(pending.current.timer);
    pending.current = null;
  };
  const saveSoon = (share: number) => {
    cancelPending();
    pending.current = { share, timer: setTimeout(() => { pending.current = null; save(share); }, KEY_SAVE_DELAY_MS) };
  };
  const shareAt = (clientX: number, from: { left: number; width: number }) => (from.width > 0 ? (clientX - from.left) / from.width : width.shown);
  const onPointerDown = (event: PointerEvent<HTMLDivElement>) => {
    drag.current = { ...width.measure(), moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    event.preventDefault();
  };
  const onPointerMove = (event: PointerEvent<HTMLDivElement>) => {
    const from = drag.current;
    if (!from) return;
    from.moved = true;
    width.draw(shareAt(event.clientX, from));
  };
  const onPointerUp = (event: PointerEvent<HTMLDivElement>) => {
    const from = drag.current;
    drag.current = null;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    // A press with no movement is half of a double press, or a click, and moves nothing.
    if (!from?.moved) return;
    cancelPending();
    save(width.draw(shareAt(event.clientX, from)));
  };
  /** 🔒 A canceled drag keeps the width it reached and saves it, so a reload draws what the person last saw (T3168). */
  const onPointerCancel = () => {
    const from = drag.current;
    drag.current = null;
    if (!from?.moved || width.share === null) return;
    cancelPending();
    save(width.share);
  };
  const reset = () => {
    cancelPending();
    width.reset();
  };
  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const frameWidth = width.measure().width;
    const step = frameWidth > 0 ? LIST_PANE_KEY_STEP_PX / frameWidth : 0.02;
    const targets: Readonly<Record<string, number>> = { ArrowLeft: width.shown - step, ArrowRight: width.shown + step, Home: 0, End: 1 };
    if (event.key === 'Enter') {
      event.preventDefault();
      return reset();
    }
    const target = targets[event.key];
    if (target === undefined) return;
    event.preventDefault();
    saveSoon(width.draw(target));
  };
  const percent = (share: number) => Math.round(share * 100);
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={t('listDetail.resize')}
      title={t('listDetail.resizeHint')}
      aria-valuemin={percent(clampListPaneShare(0, width.frameWidth))}
      aria-valuemax={percent(clampListPaneShare(1, width.frameWidth))}
      aria-valuenow={percent(width.shown)}
      tabIndex={0}
      className={styles.handle}
      data-list-pane-handle="true"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onDoubleClick={reset}
      onKeyDown={onKeyDown}
    />
  );
}

/** The route element: the record type comes from the route table, the id from the path. */
export function ListDetailRoute({ recordType, detail, apiBaseUrl = '' }: { recordType: PagedRecord; detail: (recordId: string) => ReactNode; apiBaseUrl?: string }) {
  return <ListDetailPage spec={LIST_PAGES[recordType]} detail={detail} apiBaseUrl={apiBaseUrl} />;
}
